import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Header, resolveHeaderLayout } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileNav } from "@/components/MobileNav";
import { CartDrawer } from "@/components/CartDrawer";
import { SearchOverlay } from "@/components/SearchOverlay";
import { CookieConsent } from "@/components/CookieConsent";
import { trackView } from "@/lib/track";
import { captureAdRef } from "@/lib/ads";
import { ensureGoogleTags, trackGooglePageView } from "@/lib/googleTags";
import { isStudioPreview } from "@/lib/studioPreviewBridge";
import { useTemplateSections, findEnabledSection, sectionFlag } from "@/lib/useTemplateSections";
import { getRegistryFor } from "@/themes/registry";
import useHashScroll from "@/lib/useHashScroll";
import { onThemeNavigation } from "@/lib/themeVersion";
import FloatingContactButton from "@/components/FloatingContactButton";
import { FLOATING_CONTACT_TYPE } from "@/lib/floatingContact";

// Message d'origine, codé en dur — repli tant que la section "Barre
// d'annonce" (Sprint 11, Theme Studio) n'a jamais été configurée pour cette
// boutique, ou tant que le tout premier chargement n'a pas répondu (jamais
// un flash vide). Doit rester identique à
// backend/services/theme_service.py DEFAULT_PROPS["announcement_bar"].
const DEFAULT_ANNOUNCEMENT = "Devis gratuit sous 48h · Premier appel offert · Paiement sécurisé Stripe";

// `headerSections` vaut `null` tant que le premier chargement n'a pas
// répondu (seul cas où l'on garde la barre d'origine, jamais un flash vide).
// Une fois chargé, contrairement à header_menu/footer_links, il ne faut PAS
// vérifier "le type apparaît-il dans le tableau" pour deviner un "jamais
// configuré" : "announcement_bar" fait partie de TEMPLATE_DEFAULT_ORDER
// (backend/services/theme_service.py) et est donc TOUJOURS présent dans le
// tableau renvoyé par l'API dès que ce thème n'a jamais enregistré son
// en-tête — son absence, une fois chargé, ne peut donc signifier qu'une
// chose : il a été supprimé (via l'ancienne corbeille, avant qu'elle ne soit
// retirée pour ce type) — jamais "pas encore configuré". Se fier
// uniquement à `findEnabledSection` respecte cette garantie.
export const Layout = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  // Les liens de menu en "/#ancre" ne défilaient nulle part — voir le hook.
  useHashScroll();
  const [headerSections, headerLoaded, engine] = useTemplateSections("header");
  // Chargé ici (et pas seulement dans Footer.jsx) pour pouvoir alimenter le
  // pied de page D'UN MOTEUR qui rend le sien — le cache de
  // useTemplateSections est au niveau module, donc aucun appel réseau en
  // plus quand Footer.jsx demande le même template.
  const [footerSections, footerLoaded] = useTemplateSections("footer");
  // Un moteur peut rendre lui-même l'en-tête et le pied de page (voir
  // themes/registry.js, champ CHROME). Absent — cas du moteur "default" et
  // donc de toutes les boutiques existantes — on garde EXACTEMENT le chrome
  // partagé d'aujourd'hui : barre d'annonce, Header.jsx, Footer.jsx,
  // MobileNav et les paddings calés dessus.
  const { CHROME } = getRegistryFor(engine);
  const engineChrome = Boolean(CHROME?.Header);
  const headerSection = engineChrome ? findEnabledSection(headerSections, "site_header") : null;
  const footerSection = CHROME?.Footer ? findEnabledSection(footerSections, "site_footer") : null;
  // loading = API pas encore répondue ; on garde la barre visible (layout
  // stable, le header reste décalé vers le bas) mais avec un texte vide pour
  // éviter le flash du message d'agence par défaut ("Devis gratuit sous 48h…")
  // avant que le vrai message de la boutique soit connu.
  const loading = !headerLoaded;
  const announcement = findEnabledSection(headerSections, "announcement_bar");
  // Pendant le chargement : barre présente (layout stable) mais texte vide.
  // Après chargement : barre présente seulement si la section est active.
  // Un moteur qui rend son propre en-tête rend aussi sa propre barre
  // d'annonce s'il en a une : celle du chrome partagé n'a alors plus lieu
  // d'être (le handoff « agence_v1 » n'en a pas).
  const showAnnouncement = !engineChrome && (loading || !!announcement);
  const [firstPart, ...rest] = (!loading && announcement ? (announcement.props?.message || "") : "").split(" · ");
  const restPart = rest.join(" · ");
  // Optionnel (Sprint 12) — affiché aligné à droite, en plus du message centré.
  const secondaryText = loading ? "" : (announcement?.props?.secondary_text || "");
  // Le menu du bas mobile réserve un espace en pied de page (padding) tant
  // qu'il est affiché — évite un vide à l'écran une fois désactivé (voir
  // MobileNav.jsx, même calcul, même repli tant que le réglage n'est pas
  // encore connu).
  const hideMobileNav = sectionFlag(headerSections, "header_menu", "hide_mobile_bottom_nav", false, headerLoaded);
  // En-tête transparent au-dessus du bandeau : uniquement sur l'accueil (seule
  // page dont le premier élément est le grand bandeau). Voir Header.jsx.
  const transparentHeader =
    location.pathname === "/" &&
    sectionFlag(headerSections, "header_menu", "transparent_header", false, headerLoaded);
  // Style "stacked" (logo au-dessus, menu en dessous) : l'en-tête fixe est
  // plus haut sur desktop (voir STACKED_DESKTOP_HEIGHT dans Header.jsx :
  // 72 + 48 = 120px), la page réserve donc davantage — 36px de plus avec la
  // barre d'annonce, comme pour les autres styles.
  const stackedHeader = !engineChrome && resolveHeaderLayout(headerSections, headerLoaded) === "stacked";

  useEffect(() => {
    // L'admin qui prévisualise son thème dans le Canvas du Studio de thème
    // n'est pas un vrai visiteur : ne pas polluer les stats/attributions.
    if (isStudioPreview()) return;
    trackView(location.pathname);
    captureAdRef(location.search, location.pathname);
    // Tags Google de la boutique (gtag.js / GTM) : installés au premier
    // passage, puis une vue de page par navigation (voir lib/googleTags.js).
    ensureGoogleTags();
    trackGooglePageView(location.pathname, location.search);
    // Thème republié depuis l'affichage de la page : appliqué au changement
    // de page (voir lib/themeVersion.js).
    onThemeNavigation();
  }, [location.pathname, location.search]);
  return (
    <div className="min-h-screen flex flex-col bg-cream">
      {/* Lien d'évitement — premier élément focalisable de la page. Sans lui,
          la navigation au clavier impose de traverser tout le menu avant
          d'atteindre le contenu, à chaque page. Invisible tant qu'il n'a pas
          le focus. */}
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded focus:bg-ink focus:px-4 focus:py-2 focus:text-cream"
      >
        Aller au contenu
      </a>
      {showAnnouncement && (
        <div data-testid="announce-bar" className="fixed top-0 z-50 w-full bg-ink text-cream text-center h-9 flex items-center justify-center px-4">
          <p className={`text-[10px] md:text-xs uppercase tracking-[0.15em] font-light truncate transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}>
            <span className="text-gold font-medium">{firstPart}</span>
            {restPart && <span className="hidden sm:inline">&nbsp;&nbsp;·&nbsp;&nbsp;{restPart}</span>}
          </p>
          {secondaryText && (
            <p className="hidden md:block absolute right-4 text-[10px] md:text-xs uppercase tracking-[0.15em] font-light text-cream/80 animate-fade-in">
              {secondaryText}
            </p>
          )}
        </div>
      )}
      {engineChrome ? (
        headerLoaded ? (
          // `headerSection` nul une fois chargé = l'admin a masqué la
          // section (l'œil) : on ne rend alors aucun en-tête, comme il l'a
          // demandé.
          // `data-section-key` : dans le Canvas du Studio, cliquer l'en-tête ouvre
          // la région En-tête (voir ThemeEditor/index.jsx selectFromCanvas).
          headerSection && (
            <div data-section-key="site_header" className="contents">
              <CHROME.Header props={headerSection.props} blocks={headerSection.blocks} />
            </div>
          )
        ) : (
          // Rien n'est affiché avant que les liens réels soient connus (même
          // règle que le chrome partagé : pas de flash du contenu par
          // défaut), mais la hauteur du bandeau est réservée — sinon la page
          // remonterait d'un cran au moment où il apparaît.
          <div
            className="border-b border-ink/[0.09] bg-cream"
            // Hauteur de l'en-tête du moteur (57px pour "agence_v1", plus haut
            // pour "couture_v1" et son logotype sur deux lignes) — déclarée par
            // le moteur pour que la page ne saute pas à l'apparition.
            style={{ height: CHROME.headerHeight || 57 }}
            aria-hidden="true"
          />
        )
      ) : headerLoaded ? (
        <Header onSearch={() => setSearchOpen(true)} sections={headerSections} loaded={headerLoaded} offsetTop={showAnnouncement} transparent={transparentHeader} />
      ) : (
        // Moteur pas encore connu (aucun amorçage serveur — développement,
        // ou backend injoignable au rendu du HTML) : ne JAMAIS afficher le
        // chrome par défaut en attendant. Une boutique sur un autre moteur
        // (agence_v1, couture_v1…) voyait l'en-tête ClickExpert, puis le
        // sien — c'est ce flash qu'on supprime. Hors amorçage, la page
        // reste brièvement sans en-tête ; avec, cette branche n'est jamais
        // rendue.
        null
      )}
      <main
        id="contenu"
        className={`flex-1 ${
          // Un en-tête de moteur est COLLANT, pas fixe : il occupe sa place
          // dans le flux, aucun padding compensatoire à réserver.
          engineChrome
            ? ""
            : transparentHeader
              ? (showAnnouncement ? "pt-9" : "pt-0")
              : stackedHeader
                ? (showAnnouncement ? "pt-[100px] md:pt-[156px]" : "pt-[64px] md:pt-[120px]")
                : (showAnnouncement ? "pt-[100px] md:pt-[116px]" : "pt-[64px] md:pt-[72px]")
        } ${engineChrome || hideMobileNav ? "" : "pb-[calc(4rem+env(safe-area-inset-bottom))]"} md:pb-0`}
      >
        <Outlet />
      </main>
      {CHROME?.Footer ? (
        // Même règle que l'en-tête : rien pendant le chargement, rien non
        // plus si la section a été masquée.
        footerLoaded && footerSection && (
          <div data-section-key="site_footer" className="contents">
            <CHROME.Footer props={footerSection.props} blocks={footerSection.blocks} />
          </div>
        )
      ) : (
        // Même règle : pas de pied de page d'agence avant de savoir lequel
        // rendre.
        headerLoaded && <Footer />
      )}
      {/* Bouton de contact flottant — commun à TOUS les moteurs (section
          « Bouton de contact flottant » du pied de page). */}
      {footerLoaded && (
        <FloatingContactButton
          section={findEnabledSection(footerSections, FLOATING_CONTACT_TYPE)}
          aboveMobileNav={!engineChrome && headerLoaded && !hideMobileNav}
        />
      )}
      {/* Le menu mobile du bas ferait doublon avec le menu déroulant d'un
          en-tête de moteur, qui porte déjà toute la navigation. */}
      {!engineChrome && headerLoaded && (
        <MobileNav onSearch={() => setSearchOpen(true)} sections={headerSections} loaded={headerLoaded} />
      )}
      <CartDrawer />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      {!isStudioPreview() && <CookieConsent />}
    </div>
  );
};
